import React, { useState, useEffect, useMemo } from 'react';
import { getOpenRoles } from '../../utils/api';
import OpenRolesKanban from './OpenRolesKanban';
import OpenRolesTable from './OpenRolesTable';
import OpenRolesCards from './OpenRolesCards';
import OpenRolesFilters from './OpenRolesFilters';
import OpenRolesHeader from './OpenRolesHeader';

const OpenRoles = () => {
  // State management
  const [roles, setRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState('kanban'); // 'table', 'cards', 'kanban'
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClients, setSelectedClients] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);

  // Fetch roles data
  useEffect(() => {
    const fetchRoles = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const rolesData = await getOpenRoles();
        console.log('Fetched roles data:', rolesData);
        setRoles(rolesData);
      } catch (err) {
        console.error('Error fetching open roles:', err);
        setError(err.message || 'Failed to fetch open roles');
        
        // Set fallback mock data for development
        setRoles([
          { id: '1', client: 'TechCorp', role: 'Senior Frontend Developer', department: 'Engineering', status: 'Open' },
          { id: '2', client: 'DataPrime', role: 'Product Manager', department: 'Product & Design', status: 'Open' },
          { id: '3', client: 'TechCorp', role: 'DevOps Engineer', department: 'Engineering', status: 'Open' },
          { id: '4', client: 'FinanceFlow', role: 'UX Designer', department: 'Product & Design', status: 'Open' },
          { id: '5', client: 'DataPrime', role: 'Data Scientist', department: 'Data & Analytics', status: 'Open' },
          { id: '6', client: 'TechCorp', role: 'Marketing Specialist', department: 'Marketing', status: 'Open' }
        ]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRoles();
  }, []);

  // Extract unique filter options
  const filterOptions = useMemo(() => {
    const clients = [...new Set(roles.map(role => role.client).filter(Boolean))].sort();
    const departments = [...new Set(roles.map(role => role.department).filter(Boolean))].sort();
    const statuses = [...new Set(roles.map(role => role.status).filter(Boolean))].sort();
    
    return { clients, departments, statuses };
  }, [roles]);

  // Apply filters to roles
  const filteredRoles = useMemo(() => {
    return roles.filter(role => {
      // Search term filter (searches in role title and client name)
      const matchesSearch = !searchTerm || 
        role.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
        role.client.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Multi-select filters
      const matchesClient = selectedClients.length === 0 || selectedClients.includes(role.client);
      const matchesDepartment = selectedDepartments.length === 0 || selectedDepartments.includes(role.department);
      const matchesStatus = selectedStatuses.length === 0 || selectedStatuses.includes(role.status);
      
      return matchesSearch && matchesClient && matchesDepartment && matchesStatus;
    });
  }, [roles, searchTerm, selectedClients, selectedDepartments, selectedStatuses]);

  // Handle filter changes
  const handleSearchChange = (value) => setSearchTerm(value);
  const handleClientChange = (clients) => setSelectedClients(clients);
  const handleDepartmentChange = (departments) => setSelectedDepartments(departments);
  const handleStatusChange = (statuses) => setSelectedStatuses(statuses);
  
  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedClients([]);
    setSelectedDepartments([]);
    setSelectedStatuses([]);
  };

  // CSV Export function
  const handleExportCSV = () => {
    const csvContent = [
      // Header row
      ['Client', 'Role', 'Department', 'Status'].join(','),
      // Data rows
      ...filteredRoles.map(role => [
        `"${role.client}"`,
        `"${role.role}"`, 
        `"${role.department}"`,
        `"${role.status}"`
      ].join(','))
    ].join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `open_roles_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="dashboard-card p-8 text-center fade-in">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-color mx-auto mb-6"></div>
          <p className="text-xl font-medium text-gray-700">Loading open roles...</p>
          <p className="text-sm text-gray-500 mt-2">Fetching the latest opportunities</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Header */}
        <OpenRolesHeader 
          totalRoles={roles.length}
          filteredRoles={filteredRoles.length}
          currentView={view}
          onViewChange={setView}
          onExportCSV={handleExportCSV}
          error={error}
        />

        {/* Filters */}
        <OpenRolesFilters 
          searchTerm={searchTerm}
          selectedClients={selectedClients}
          selectedDepartments={selectedDepartments}
          selectedStatuses={selectedStatuses}
          filterOptions={filterOptions}
          onSearchChange={handleSearchChange}
          onClientChange={handleClientChange}
          onDepartmentChange={handleDepartmentChange}
          onStatusChange={handleStatusChange}
          onClearFilters={handleClearFilters}
        />

        {/* Main Content */}
        <div className="fade-in">
          {view === 'kanban' && (
            <OpenRolesKanban roles={filteredRoles} />
          )}
          {view === 'table' && (
            <OpenRolesTable roles={filteredRoles} />
          )}
          {view === 'cards' && (
            <OpenRolesCards roles={filteredRoles} />
          )}
        </div>
      </div>
    </div>
  );
};

export default OpenRoles;